<template lang="pug">
.form-builder(:class="{ 'is-edit': edit }")
    //- ============ EDIT MODE ============
    template(v-if="edit")
        .fb-edit
            .fb-edit-side
                form-builder-palette(vertical :used-types="usedTypes" @add="addElement")
            .fb-edit-main
                form-builder-list.fb-edit-list(
                    :list="elements"
                    :selected-id="selectedId"
                    :edit="true"
                    :options="options"
                    @change="emitChange"
                    @select="selectElement"
                    @delete="deleteElement"
                )
                .fb-info-bar
                    el-button.fb-info-btn(
                        size="mini"
                        type="primary"
                        plain
                        :class="{ 'is-active': showInfo }"
                        @click="showInfo = !showInfo"
                    )
                        i.el-icon-fa-question-circle.fas
                        | &nbsp;Hilfe &amp; Anleitung
                transition(name="fb-info-slide")
                    .fb-info-content(v-if="showInfo")
                        button.fb-info-close(@click="showInfo = false")
                            i.el-icon-close
                        h4 Formular-Generator — Anleitung
                        .fb-info-intro So baust du dein Formular: Felder aus der Palette (links) anklicken, im Bereich rechts anordnen und je Feld die Einstellungen anpassen. Die wichtigsten Einstellungen für Versand und Aussehen findest du in den Section-Einstellungen (Tab „Einstellungen").
                        .fb-info-grid
                            .fb-info-section
                                .fb-info-title
                                    i.el-icon-fa-plus.fas
                                    | &nbsp;Felder hinzufügen
                                ul
                                    li Element in der Palette (links) anklicken – es wird unten an das Formular angehängt
                                    li
                                        b Felder:
                                        |  Name, E-Mail, Text, Textfeld, Auswahl, Land, Datum, Schieberegler, Datei-Upload, Zustimmung
                                    li
                                        b Inhalt:
                                        |  Überschrift und Textabsatz – für Zwischentitel und Erklärungen im Formular
                                    li
                                        b Layout:
                                        |  Spalten – teilt das Formular in mehrere nebeneinanderliegende Spalten
                                    li Manche Felder (z. B. Name, E-Mail, Zustimmung) lassen sich nur einmal einfügen – sie sind danach in der Palette ausgegraut
                            .fb-info-section
                                .fb-info-title
                                    i.el-icon-fa-arrows-alt.fas
                                    | &nbsp;Anordnen &amp; Sortieren
                                ul
                                    li Feld anklicken → seine Einstellungen erscheinen rechts daneben
                                    li Reihenfolge per Drag &amp; Drop am Griff (&#9776;) ändern
                                    li Felder in ein Spalten-Element ziehen und dort zwischen den Spalten verschieben
                                    li Das rote Papierkorb-Symbol entfernt ein Feld
                            .fb-info-section
                                .fb-info-title
                                    i.el-icon-fa-lock.fas
                                    | &nbsp;Pflichtfelder: E-Mail &amp; Absenden
                                ul
                                    li
                                        b E-Mail
                                        |  kann nicht gelöscht werden: An diese vom Besucher eingegebene Adresse geht die Bestätigungskopie, und sie dient als Antwortadresse. Ohne sie wäre keine Rückmeldung möglich.
                                    li
                                        b Absenden
                                        |  kann nicht gelöscht werden: Ohne den Button ließe sich das Formular nicht abschicken. Er steht immer am Ende.
                                    li Gesperrte Felder haben kein Papierkorb-Symbol – Beschriftung und Optionen lassen sich aber weiterhin anpassen
                            .fb-info-section
                                .fb-info-title
                                    i.el-icon-fa-cog.fas
                                    | &nbsp;Feld-Einstellungen
                                ul
                                    li Je Feld: Beschriftung, Platzhalter und ob es ein Pflichtfeld ist
                                    li
                                        b Rolle:
                                        |  bestimmt bei Textfeldern den Eingabetyp (Telefon, URL, Nummer …)
                                    li
                                        b Auswahl / Optionsfelder / Checkboxen:
                                        |  eigene Optionen pflegen, wahlweise neben- oder untereinander
                                    li
                                        b Datum:
                                        |  Modus „Datum", „Datum &amp; Uhrzeit" oder „Uhrzeit" sowie frühestes/spätestes Datum
                                    li
                                        b Zustimmung:
                                        |  die Datenschutz-Seite wird automatisch erkannt und im Text verlinkt
                            .fb-info-section
                                .fb-info-title
                                    i.el-icon-fa-arrows-alt-h.fas
                                    | &nbsp;Breite &amp; Position (Einstellungen)
                                ul
                                    li
                                        b Formularbreite:
                                        |  Wert in % oder px (z. B. 100%, 80%, 600px). Standard ist 100% = volle Breite
                                    li
                                        b Position:
                                        |  erscheint, sobald die Breite unter 100% liegt – richtet das Formular links, zentriert oder rechts aus
                                    li Diese Optionen liegen im Tab „Einstellungen" unter „Allgemeine Einstellungen"
                            .fb-info-section
                                .fb-info-title
                                    i.el-icon-fa-envelope.fas
                                    | &nbsp;E-Mail-Benachrichtigung (Einstellungen)
                                ul
                                    li
                                        b Bestätigungskopie an Absender:
                                        |  schickt dem Besucher automatisch eine Kopie seiner Eingaben
                                    li
                                        b E-Mail-Benachrichtigung senden:
                                        |  verschickt bei jedem Eingang eine Benachrichtigung an dein Team
                                    li
                                        b Benachrichtigung an / Cc / Bcc:
                                        |  Empfänger der Benachrichtigung – mehrere Adressen mit Komma trennen
                                    li
                                        b Betreff:
                                        |  eigene Betreffzeile; leer lassen für den Standard-Betreff
    //- ============ PREVIEW MODE ============
    .fb-preview(v-else :class="{ 'fb-labels-left': labelPosition === 'left' }" :style="previewStyle")
        .fb-preview-blocks
            template(v-for="block in elements")
                .fb-preview-columns(
                    v-if="block.type === 'columns'"
                    :key="block.id"
                    :style="{ gridTemplateColumns: `repeat(${block.children.length}, 1fr)` }"
                )
                    .fb-preview-column(v-for="(column, ci) in block.children" :key="ci")
                        form-builder-preview-field(
                            v-for="element in column"
                            :key="element.id"
                            :element="element"
                        )
                form-builder-preview-field(v-else :key="block.id" :element="block")
</template>

<script>
import _ from 'lodash'
import FormBuilderPalette from './FormBuilderPalette'
import FormBuilderList from './FormBuilderList'
import FormBuilderPreviewField from './FormBuilderPreviewField'
import { createElement, elementsToContents, contentsToElements } from './palette'

export default {
    name: 'FormBuilder',
    components: { FormBuilderPalette, FormBuilderList, FormBuilderPreviewField },
    props: {
        // the whole section whose `contents` this builder reads/writes (relational);
        // in edit mode this is the drawer's working clone, so the standard
        // whole-section save persists the rows we mutate here
        section: { type: Object, required: true },
        edit: { type: Boolean, default: false },
        options: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            // a flat list of blocks; a block is either a field element or a
            // "columns" container { type:'columns', children: [[...],[...]] }
            elements: [],
            selectedId: null,
            // signature of the contents we last wrote, to ignore our own writes in
            // the section.contents watcher (rebuild only on external changes)
            writtenSignature: '',
            seeded: false,
            showInfo: false,
        }
    },
    computed: {
        usedTypes() {
            return this.collectTypes(this.elements, [])
        },
        // flat list of all selectable menu items (for privacy-page auto-detect)
        allMenuItems() {
            const menuOptions = _.get(this.options, 'menu.items') || []
            return menuOptions.reduce((acc, mo) => acc.concat(mo.menuItems || []), [])
        },
        // mirror the storefront label-position / label-width settings in the preview
        labelPosition() {
            return _.get((this.section.settings || []).find(s => s.key === 'label-position'), 'value') || 'top'
        },
        labelWidth() {
            const raw = String(_.get((this.section.settings || []).find(s => s.key === 'label-width'), 'value') || '10rem').trim()
            if (!raw) return '10rem'
            return /^\d+(\.\d+)?$/.test(raw) ? `${raw}px` : raw
        },
        previewStyle() {
            return { '--fb-label-width': this.labelWidth }
        },
    },
    mounted() {
        // build the editing tree from the section's relational contents, then seed
        // the standard fields for a brand-new (empty) form
        this.syncFromContents()
        this.maybeSeedDefaults()
    },
    watch: {
        // rebuild the tree when contents change OUTSIDE this builder (e.g. the save
        // response swaps `new:` ids for real ones); our own writes are ignored via
        // the signature guard so this never loops.
        'section.contents': {
            deep: true,
            handler() {
                if (this.signature(this.section.contents) === this.writtenSignature) return
                this.syncFromContents()
            },
        },
        // `edit` may only flip to true after mount (the drawer mounts the component
        // before it is in edit state); seed the standard fields on entering edit.
        edit(isEdit) {
            if (isEdit) this.maybeSeedDefaults()
        },
    },
    methods: {
        // migrate legacy 'multi-select' fields to a 'select' with multiple = true
        normalizeBlocks(list) {
            list.forEach(block => {
                if (block.type === 'multi-select') {
                    block.type = 'select'
                    block.multiple = true
                }
                if (block.type === 'columns' && Array.isArray(block.children)) {
                    block.children.forEach(column => this.normalizeBlocks(column))
                }
            })
        },
        collectTypes(list, acc) {
            list.forEach(block => {
                acc.push(block.type)
                if (block.type === 'columns') {
                    block.children.forEach(column => this.collectTypes(column, acc))
                }
            })
            return acc
        },
        // Preselect a privacy page ("Datenschutz…") for every consent field whose
        // link is still untouched (undefined). This mirrors the auto-detect in the
        // element settings, but runs on load/seed so a consent field that is never
        // opened still gets its link – otherwise the storefront renders no link.
        // A deliberately cleared link is null and stays that way. Returns true if
        // any element was changed.
        autoDetectConsentLinks(list) {
            let changed = false
            const walk = items => items.forEach(block => {
                if (block.type === 'consent' && block.link === undefined) {
                    const match = this.allMenuItems.find(item => /datenschutz/i.test(item.title || ''))
                    this.$set(block, 'link', match ? { id: match.id, title: match.title, path: match.path } : null)
                    if (match) changed = true
                }
                if (block.type === 'columns' && Array.isArray(block.children)) {
                    block.children.forEach(column => walk(column))
                }
            })
            walk(list)
            return changed
        },
        // Freshly created form (no stored value yet) → seed the standard fields
        // Name / E-Mail / Nachricht / Datenschutz / Absenden so the user starts with a
        // usable contact form instead of a blank canvas.
        defaultElements() {
            return ['name', 'email', 'textarea', 'consent', 'submit']
                .map(type => createElement(type))
                .filter(Boolean)
        },
        // Pre-fill the standard fields only for a brand-new form: in edit mode,
        // with no stored value and nothing already on the canvas. A deliberately
        // emptied, saved form has a non-empty value ('{"version":2,"elements":[]}')
        // and is therefore left untouched. The `seeded` guard makes this run at
        // most once, so the round-trip of the emit (which turns `value` into the
        // seeded JSON) can never re-trigger it.
        maybeSeedDefaults() {
            const hasContents = Array.isArray(this.section.contents) && this.section.contents.length
            if (this.seeded || !this.edit || hasContents || this.elements.length) return
            this.seeded = true
            this.elements = this.defaultElements()
            this.autoDetectConsentLinks(this.elements)
            this.selectedId = null
            // render the defaults immediately; persist them into section.contents on
            // the next tick so the first paint isn't disturbed.
            if (this.elements.length) this.$nextTick(() => this.emitChange())
        },
        // build the editing tree from the section's relational contents
        syncFromContents() {
            this.elements = contentsToElements(this.section.contents || [])
            this.normalizeBlocks(this.elements)
            this.writtenSignature = this.signature(this.section.contents || [])
            // fill in the privacy link for consent fields stored without one
            if (this.edit && this.autoDetectConsentLinks(this.elements)) {
                this.$nextTick(() => this.emitChange())
            }
            this.selectedId = null
        },
        // stable signature of a contents array, to tell our own writes apart from
        // external changes in the deep watcher
        signature(contents) {
            try {
                return JSON.stringify(contents || [])
            } catch (_) {
                return ''
            }
        },
        // serialise the editing tree back into the section's relational contents;
        // the standard whole-section save then persists them
        emitChange() {
            const contents = elementsToContents(this.elements)
            this.writtenSignature = this.signature(contents)
            this.$set(this.section, 'contents', contents)
        },
        addElement(type) {
            const element = createElement(type)
            if (!element) return
            // keep the submit button at the very bottom: a new field is inserted
            // above a trailing submit instead of after it
            const last = this.elements[this.elements.length - 1]
            if (last && last.type === 'submit' && element.type !== 'submit') {
                this.elements.splice(this.elements.length - 1, 0, element)
            } else {
                this.elements.push(element)
            }
            // containers have no inline settings; only auto-open fields
            this.selectedId = element.type === 'columns' ? null : element.id
            this.emitChange()
        },
        selectElement(element) {
            this.selectedId = this.selectedId === element.id ? null : element.id
        },
        deleteElement(element) {
            this.removeById(this.elements, element.id)
            if (this.selectedId === element.id) this.selectedId = null
            this.emitChange()
        },
        removeById(list, id) {
            for (let i = 0; i < list.length; i++) {
                if (list[i].id === id) {
                    list.splice(i, 1)
                    return true
                }
                if (list[i].type === 'columns') {
                    for (const column of list[i].children) {
                        if (this.removeById(column, id)) return true
                    }
                }
            }
            return false
        },
    },
}
</script>

<style lang="sass" scoped>
.form-builder
    width: 100%
// ---- help toggle: directly below the edit list, right-aligned to its edge ----
.fb-info-bar
    display: flex
    justify-content: flex-end
    margin-top: .75rem
.fb-info-btn
    &.is-active
        background: #ecf5ff !important
// ---- help / info panel ----
.fb-info-content
    position: relative
    background: #f0f7ff
    border: 1px solid #c6e0ff
    border-radius: 6px
    padding: 20px 24px 18px
    margin-top: .5rem
    margin-bottom: .75rem
    font-size: 13px
    line-height: 1.7
    color: #4b5563
    h4
        margin: 0 0 16px
        font-size: 15px
        font-weight: 700
        color: #1e3a5f
        border-bottom: 2px solid #c6e0ff
        padding-bottom: 10px
    ul
        margin: 6px 0 0
        padding-left: 18px
        li
            margin-bottom: 5px
            font-size: 13px
    b
        color: #1e3a5f
.fb-info-intro
    margin: 0 0 14px
    font-size: 13px
    color: #4b5563
.fb-info-grid
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr))
    gap: 16px 32px
.fb-info-section
    .fb-info-title
        display: flex
        align-items: center
        gap: 5px
        font-weight: 700
        color: #2563eb
        font-size: 12px
        text-transform: uppercase
        letter-spacing: 0.06em
        margin-bottom: 6px
.fb-info-close
    position: absolute
    top: 12px
    right: 14px
    background: none
    border: none
    cursor: pointer
    color: #909399
    font-size: 18px
    line-height: 1
    padding: 2px
    &:hover
        color: #303133
.fb-info-slide-enter-active,
.fb-info-slide-leave-active
    transition: all .2s ease
.fb-info-slide-enter,
.fb-info-slide-leave-to
    opacity: 0
    transform: translateY(-6px)
// ---- edit layout: palette left, canvas right ----
.fb-edit
    display: flex
    align-items: flex-start
    gap: .75rem
.fb-edit-side
    flex: 0 0 190px
    position: sticky
    top: .5rem
// right column: the canvas (edit list) plus the help toggle directly beneath it
.fb-edit-main
    flex: 1 1 auto
    min-width: 0
.fb-edit-list
    min-width: 0
// ---- preview ----
// the preview is display-only; make it click-through so a click anywhere on the
// section (incl. the disabled preview inputs) reaches the section's edit handler
.fb-preview
    pointer-events: none
// left-aligned labels in the preview: fixed-width label column (mirrors storefront)
.fb-preview.fb-labels-left
    ::v-deep .fb-preview-field.has-label
        display: grid
        grid-template-columns: var(--fb-label-width, 10rem) minmax(0, 1fr)
        align-items: baseline
        column-gap: .75rem
        > .fb-preview-label
            margin: 0
            grid-column: 1
        > *:not(.fb-preview-label)
            grid-column: 2
.fb-preview-columns
    display: grid
    gap: 1rem
</style>
